import React from 'react';
import { BackOfficeTrailBasicInfoResp } from 'domain/payload/response/backOfficeTrails/BackOfficeTrailsResp';

interface BackOfficeBasicInfoCardProps {
    basicInfo: BackOfficeTrailBasicInfoResp | null;
    loading?: boolean;
}

const BackOfficeBasicInfoCard: React.FC<BackOfficeBasicInfoCardProps> = ({
    basicInfo,
    loading = false,
}) => {
    const handleWhatsAppClick = (phoneNumber?: string | number | null) => {
        if (!phoneNumber) return;
        const strPhone = String(phoneNumber);
        const cleanNumber = strPhone.replace(/\D/g, '');
        let whatsappNumber = cleanNumber;

        if (whatsappNumber.startsWith('0')) {
            whatsappNumber = '62' + whatsappNumber.substring(1);
        }

        window.open(`https://wa.me/${whatsappNumber}`, '_blank');
    };

    const formatDateTime = (dateStr?: string | null): string => {
        if (!dateStr) return 'N/A';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            const day = d.getDate();
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[d.getMonth()];
            const year = d.getFullYear();
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
        } catch {
            return dateStr;
        }
    };

    const formatDateOnly = (dateStr?: string | null): string => {
        if (!dateStr) return 'N/A';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            const day = d.getDate();
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[d.getMonth()];
            const year = d.getFullYear();
            return `${day} ${month} ${year}`;
        } catch {
            return dateStr;
        }
    };

    return (
        <div className="card h-100 border shadow-none">
            <div className="card-header bg-light border-bottom py-3 px-4">
                <h6 className="mb-0 fw-bold">Basic Info</h6>
            </div>
            <div className="card-body p-4">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="d-flex flex-column align-items-center">
                        <div className="mb-3">
                            <div
                                className="bg-light rounded-circle d-flex align-items-center justify-content-center overflow-hidden border shadow-sm"
                                style={{ width: '100px', height: '100px' }}
                            >
                                {basicInfo?.profilePicture ? (
                                    <img
                                        src={basicInfo.profilePicture}
                                        alt="Profile"
                                        className="rounded-circle"
                                        style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <i className="fa-solid fa-user fa-3x text-secondary"></i>
                                )}
                            </div>
                        </div>

                        <h4 className="fw-bold mb-4">{basicInfo?.name || 'N/A'}</h4>

                        <div className="w-100">
                            <div className="row g-3">
                                <div className="col-12">
                                    <div className="d-flex align-items-start">
                                        <div className="me-3 text-muted" style={{ width: '24px', textAlign: 'center' }}>
                                            <i className="fa-regular fa-envelope fa-lg"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small mb-1">Email</div>
                                            <div className="fw-semibold text-dark">{basicInfo?.email || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="d-flex align-items-start">
                                        <div className="me-3 text-muted" style={{ width: '24px', textAlign: 'center' }}>
                                            <i className="fa-solid fa-phone fa-lg"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small mb-1">Phone</div>
                                            {basicInfo?.phone ? (
                                                <div
                                                    className="fw-semibold text-dark d-flex align-items-center"
                                                    onClick={() => handleWhatsAppClick(basicInfo.phone)}
                                                    style={{ cursor: 'pointer' }}
                                                    title="Click to chat on WhatsApp"
                                                >
                                                    {basicInfo.phone}
                                                    <i className="fa-brands fa-whatsapp ms-2 text-success fa-lg"></i>
                                                </div>
                                            ) : (
                                                <div className="text-muted">No phone number</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="d-flex align-items-start">
                                        <div className="me-3 text-muted" style={{ width: '24px', textAlign: 'center' }}>
                                            <i className="fa-solid fa-clock-rotate-left fa-lg"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small mb-1">Last Access</div>
                                            <div className="fw-semibold text-dark">
                                                {formatDateTime(basicInfo?.lastAccess)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="d-flex align-items-start">
                                        <div className="me-3 text-muted" style={{ width: '24px', textAlign: 'center' }}>
                                            <i className="fa-regular fa-calendar-days fa-lg"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small mb-1">Created Date</div>
                                            <div className="fw-semibold text-dark">
                                                {formatDateOnly(basicInfo?.createdAt)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="d-flex align-items-start">
                                        <div className="me-3 text-muted" style={{ width: '24px', textAlign: 'center' }}>
                                            <i className="fa-solid fa-user-group fa-lg"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small mb-1">Created By</div>
                                            <div className="fw-semibold text-dark">
                                                {basicInfo?.createdBy || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BackOfficeBasicInfoCard;
